import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import {
  encryptionLabels,
  mailboxStatusLabel,
  type ConnectionCheck,
  type ConnectionDirection,
  type Encryption,
} from "../entities/mailboxes";
import { formatDate } from "./counterpartiesSupport";
import { MailboxForm, type MailboxFormValues } from "./MailboxForm";
import { MailboxNotice } from "./MailboxNotice";
import { MailboxSyncPanel } from "./MailboxSyncPanel";
import { mailboxKeys, type OrganizationName } from "./mailboxesSupport";
import type { ShellServices } from "./services";
import { Select } from "./Select";

type Service = NonNullable<ShellServices["mailboxes"]>;

const directionOptions: readonly {
  value: ConnectionDirection;
  label: string;
}[] = [
  { value: "both", label: "IMAP и SMTP" },
  { value: "imap", label: "Только IMAP" },
  { value: "smtp", label: "Только SMTP" },
];

const connectionCodeLabels: Record<string, string> = {
  "document_workflow.mailbox_unavailable":
    "Почтовый сервер не ответил или отклонил вход",
};

function encryption(value: string): string {
  return encryptionLabels[value as Encryption] ?? value;
}

/** Статус ящика цветом и словом: по цвету одному состояние не различить. */
export function MailboxStatusBadge({
  status,
  enabled,
}: {
  status: string;
  enabled: boolean;
}) {
  const tone = !enabled
    ? "disabled"
    : status === "healthy"
      ? "ready"
      : status === "failed" || status === "error"
        ? "rejected"
        : "pending";
  return (
    <span className={`status-badge status-badge--${tone}`}>
      {mailboxStatusLabel(status, enabled)}
    </span>
  );
}

function ConnectionTest({
  service,
  mailboxId,
}: {
  service: Service;
  mailboxId: string;
}) {
  const [direction, setDirection] = useState<ConnectionDirection>("both");
  const [result, setResult] = useState<ConnectionCheck | null>(null);
  const check = useMutation({
    mutationFn: () => service.testConnection(mailboxId, direction),
    onMutate: () => setResult(null),
    onSuccess: setResult,
  });

  return (
    <section
      className="cp-section mb-connection"
      aria-labelledby="mb-test-title"
    >
      <div className="mb-section-head">
        <h3 id="mb-test-title">Проверка соединения</h3>
      </div>
      <div className="mb-inline">
        <label className="field cp-field">
          <span>Что проверить</span>
          <Select
            value={direction}
            onChange={(event) =>
              setDirection(event.target.value as ConnectionDirection)
            }
          >
            {directionOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </label>
        <button
          type="button"
          className="button button--secondary button--compact"
          disabled={check.isPending}
          onClick={() => check.mutate()}
        >
          {check.isPending ? "Проверяем…" : "Проверить соединение"}
        </button>
      </div>
      {check.isError ? (
        <MailboxNotice
          error={check.error}
          fallback="Не удалось выполнить проверку."
          onRetry={() => check.mutate()}
          retrying={check.isPending}
        />
      ) : null}
      {result === null ? null : result.ok ? (
        <p className="mb-result mb-result--ok" role="status">
          Соединение работает · проверено {formatDate(result.checked_at)}
        </p>
      ) : (
        <p className="mb-result mb-result--fail" role="status">
          Соединение не установлено ·{" "}
          {(result.code === null
            ? undefined
            : connectionCodeLabels[result.code]) ??
            result.code ??
            "причина не указана"}
          . Проверьте адрес сервера, порт, шифрование и доступ к ящику.
        </p>
      )}
    </section>
  );
}

/** Включение и отключение. Удаления ящика в Core нет, и интерфейс его не изображает. */
function EnabledToggle({
  service,
  mailboxId,
  enabled,
}: {
  service: Service;
  mailboxId: string;
  enabled: boolean;
}) {
  const client = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const toggle = useMutation({
    mutationFn: () => service.update(mailboxId, { enabled: !enabled }),
    onSuccess: async (updated) => {
      setConfirming(false);
      client.setQueryData(mailboxKeys.detail(mailboxId), updated);
      await client.invalidateQueries({
        queryKey: [...mailboxKeys.all, "list"],
      });
    },
  });

  return (
    <div className="mb-toggle">
      {enabled && !confirming ? (
        <button
          type="button"
          className="button button--secondary button--compact"
          onClick={() => setConfirming(true)}
        >
          Отключить ящик
        </button>
      ) : null}
      {!enabled ? (
        <button
          type="button"
          className="button button--secondary button--compact"
          disabled={toggle.isPending}
          onClick={() => toggle.mutate()}
        >
          {toggle.isPending ? "Включаем…" : "Включить ящик"}
        </button>
      ) : null}
      {enabled && confirming ? (
        <div
          className="mb-confirm"
          role="group"
          aria-label="Подтверждение отключения"
        >
          <p>
            Ящик будет отключён, но не удалён: письма, история и настройки
            сохранятся. Синхронизация и отправка через него прекратятся, пока вы
            его не включите.
          </p>
          <div className="dw-form-actions">
            <button
              type="button"
              className="button button--danger button--compact"
              disabled={toggle.isPending}
              onClick={() => toggle.mutate()}
            >
              {toggle.isPending ? "Отключаем…" : "Отключить"}
            </button>
            <button
              type="button"
              className="button button--text button--compact"
              disabled={toggle.isPending}
              onClick={() => setConfirming(false)}
            >
              Не отключать
            </button>
          </div>
        </div>
      ) : null}
      {toggle.isError ? (
        <MailboxNotice
          error={toggle.error}
          fallback="Не удалось изменить состояние ящика."
          onRetry={() => toggle.mutate()}
          retrying={toggle.isPending}
        />
      ) : null}
    </div>
  );
}

export function MailboxCard({
  service,
  id,
  canSync,
  canViewFailures,
  canSelectSystemSender,
  onBack,
  onShowFailures,
  organizationName,
}: {
  service: Service;
  id: string;
  canSync: boolean;
  canViewFailures: boolean;
  canSelectSystemSender: boolean;
  onBack: () => void;
  onShowFailures: (mailboxId: string) => void;
  organizationName: OrganizationName;
}) {
  const client = useQueryClient();
  const [editing, setEditing] = useState(false);
  const mailbox = useQuery({
    queryKey: mailboxKeys.detail(id),
    queryFn: () => service.get(id),
    retry: false,
  });
  const systemSender = useMutation({
    mutationFn: (selected: boolean) =>
      service.update(id, { is_system_sender: selected }),
    onSuccess: async (updated) => {
      client.setQueryData(mailboxKeys.detail(id), updated);
      await client.invalidateQueries({
        queryKey: [...mailboxKeys.all, "list"],
      });
      await client.invalidateQueries({ queryKey: mailboxKeys.all });
    },
  });
  const update = useMutation({
    // Вариант мутации хранит введённый пароль; в кэше он не должен задерживаться.
    gcTime: 0,
    mutationFn: (values: Extract<MailboxFormValues, { mode: "edit" }>) =>
      service.update(id, values.patch),
    onSuccess: async (updated) => {
      // Ответ Core — единственный источник: введённый секрет в кэш не попадает.
      client.setQueryData(mailboxKeys.detail(id), updated);
      setEditing(false);
      update.reset();
      await client.invalidateQueries({
        queryKey: [...mailboxKeys.all, "list"],
      });
    },
  });

  if (mailbox.isLoading) return <p aria-busy="true">Загружаем ящик…</p>;
  if (mailbox.isError || mailbox.data === undefined) {
    return (
      <div className="cp-card">
        <button
          type="button"
          className="button button--text cp-back"
          onClick={onBack}
        >
          ← К списку
        </button>
        <MailboxNotice
          error={mailbox.error}
          fallback="Не удалось загрузить ящик."
          onRetry={() => void mailbox.refetch()}
          retrying={mailbox.isFetching}
        />
      </div>
    );
  }

  const data = mailbox.data;

  if (editing) {
    return (
      <div className="cp-card">
        <MailboxForm
          mailbox={data}
          pending={update.isPending}
          error={update.error}
          onChange={() => update.reset()}
          onCancel={() => {
            update.reset();
            setEditing(false);
          }}
          onSubmit={(values) => {
            if (values.mode === "edit") update.mutate(values);
          }}
        />
      </div>
    );
  }

  return (
    <article className="cp-card mb-card" aria-labelledby="mb-card-title">
      <button
        type="button"
        className="button button--text cp-back"
        onClick={onBack}
      >
        ← К списку
      </button>
      <div className="cp-card__head">
        <div>
          <h2 id="mb-card-title">{data.name}</h2>
          <p className="muted">{data.email}</p>
          <p className="mb-org">
            {organizationName(data.access_scope_id) ?? "Организация недоступна"}
          </p>
        </div>
        <MailboxStatusBadge status={data.status} enabled={data.enabled} />
      </div>

      <div className="mb-actions">
        <button
          type="button"
          className="button button--primary button--compact"
          onClick={() => setEditing(true)}
        >
          Изменить настройки
        </button>
        <EnabledToggle
          service={service}
          mailboxId={data.id}
          enabled={data.enabled}
        />
      </div>

      <section className="cp-section" aria-labelledby="mb-system-sender-title">
        <h3 id="mb-system-sender-title">Системные письма</h3>
        <p role="status">
          {data.is_system_sender
            ? "Этот ящик назначен отправителем системных писем для всей ERP."
            : "Этот ящик не назначен отправителем системных писем."}
        </p>
        <p className="muted">
          Через отправителя уходят приглашения, сброс пароля и уведомления. Если
          ящик выключен или недоступен, ERP использует резервный SMTP, когда он
          настроен.
        </p>
        {canSelectSystemSender ? (
          <button
            type="button"
            className="button button--secondary button--compact"
            disabled={
              systemSender.isPending ||
              (!data.enabled && !data.is_system_sender)
            }
            onClick={() => systemSender.mutate(!data.is_system_sender)}
          >
            {systemSender.isPending
              ? "Сохраняем…"
              : data.is_system_sender
                ? "Снять назначение отправителя"
                : "Назначить отправителем системных писем"}
          </button>
        ) : null}
        {systemSender.isError ? (
          <MailboxNotice
            error={systemSender.error}
            fallback="Не удалось изменить отправителя системных писем."
          />
        ) : null}
      </section>

      {data.last_error_code === null ? null : (
        <div className="mb-result mb-result--fail" role="status">
          <p>
            Последняя ошибка ящика: <code>{data.last_error_code}</code>
          </p>
          {canViewFailures ? (
            <button
              type="button"
              className="button button--text button--compact mb-result__action"
              onClick={() => onShowFailures(data.id)}
            >
              Открыть ошибки этого ящика
            </button>
          ) : null}
        </div>
      )}

      <dl className="cp-requisites" aria-label="Настройки ящика">
        <div>
          <dt>Входящая почта (IMAP)</dt>
          <dd>
            {data.imap.host}:{data.imap.port} ·{" "}
            {encryption(data.imap.encryption)}
          </dd>
        </div>
        <div>
          <dt>Папка</dt>
          <dd>{data.imap.folder}</dd>
        </div>
        <div>
          <dt>Исходящая почта (SMTP)</dt>
          <dd>
            {data.smtp.host}:{data.smtp.port} ·{" "}
            {encryption(data.smtp.encryption)}
          </dd>
        </div>
        <div>
          <dt>Логин</dt>
          <dd>{data.username}</dd>
        </div>
        <div>
          <dt>Доступ к ящику</dt>
          <dd>
            {data.has_credential_reference
              ? "Ссылка на секрет задана"
              : "Секрет не задан"}
            <small className="cp-hint mb-secret-note">
              Значение секрета не хранится в интерфейсе и не показывается.
            </small>
          </dd>
        </div>
        <div>
          <dt>Последняя успешная синхронизация</dt>
          <dd>{formatDate(data.last_successful_sync_at)}</dd>
        </div>
      </dl>

      <ConnectionTest service={service} mailboxId={data.id} />
      <MailboxSyncPanel
        service={service}
        mailboxId={data.id}
        enabled={data.enabled}
        canSync={canSync}
      />
    </article>
  );
}
